import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {TemplateItem} from 'app/Model/Catalog/TemplateItem';
import {NextPimField} from 'app/Model/Catalog/NextPimField';
import {ExcelElement} from 'app/Model/ui/ExcelElement';
import {Select} from 'app/Model/ui/Select';
import {WawiList} from 'app/Model/wawi/WawiList';
import {FormulaValidationResult, TemplateService} from 'app/Services/template.service';
import {ValueChangedEvent} from 'devextreme/ui/switch';

type FormulaPlaceholder = { name: string; label: string };

@Component({
  selector: 'np-advanced-template-fieldsettings',
  templateUrl: './advanced-template-fieldsettings.component.html',
  styleUrls: [],
  standalone: false
})
export class AdvancedTemplateFieldsettingsComponent implements OnInit {
  @Input() showPlaceHolders = true;
  @Input() excelElement: ExcelElement;
  @Input() templateItem: TemplateItem;
  @Input() set mappings(value: WawiList[]) {
    this._mappings = [new WawiList('', '', '', '', false, []), ...(value ?? [])];
  }
  get mappings(): WawiList[] { return this._mappings; }
  private _mappings: WawiList[] = [];
  @Input() exportMode = true;
  @Input() showFormulaMode = false;

  @Output() onUpdate = new EventEmitter<object>();

  mappingApplicationOptions: Select[] = [];
  formulaError: FormulaValidationResult = null;
  private formulaPlaceholderCache: { key: string; items: FormulaPlaceholder[] } = null;

  calculation: Select[] = [];
  numberformat: Select[] = [];
  dateformat: Select[] = [];
  seperator: Select[] = [];
  operators: Select[] = [];

  splitModeOptions: Select[] = [];
  splitSeparator: Select[] = [];

  displayExpr: string = '';

  public splitSeparatorValue: string = '';

  constructor(private translate: TranslateService, private templateService: TemplateService) {}

  get isFormulaActive(): boolean {
    return this.showFormulaMode && !!this.templateItem?.isFormulaMode;
  }

  get formulaPlaceholders(): FormulaPlaceholder[] {
    const fields = this.templateItem?.pimFields ?? [];
    const key = fields.map((field) => `${field.fieldType}|${field.field}|${field.systemKey}|${field.elementKey}|${field.value}`).join(';');
    if (this.formulaPlaceholderCache?.key !== key) {
      this.formulaPlaceholderCache = {
        key,
        items: fields.map((field, index) => ({ name: `Feld${index + 1}`, label: this.getFieldLabel(field) }))
      };
    }
    return this.formulaPlaceholderCache.items;
  }

  formulaPlaceholderLabel = (item: FormulaPlaceholder): string => (item ? `${item.name} – ${item.label}` : '');

  formulaPlaceholderName = (item: FormulaPlaceholder): string => item?.name ?? '';

  onFormulaModeChanged(event: ValueChangedEvent): void {
    if (event.event && this.templateItem.isFormulaMode) {
      this.resetColumnSettings();
    }
    this.formulaError = null;
    this.update();
  }

  async onFormulaChanged(formula: string): Promise<void> {
    this.templateItem.formula = formula;
    this.update();

    const result = await this.templateService.validateExportFormula(formula, this.templateItem.pimFields.length);
    this.formulaError = result.success ? null : result;
  }

  private getFieldLabel(field: NextPimField): string {
    if (field.fieldType === 'fixed') {
      return `${this.translate.instant('Festwert')}: ${field.value ?? ''}`;
    }

    const keys = [field.systemKey, field.elementKey].filter((key) => key && key !== '-1' && key !== '0');
    const fieldName = field.field ? this.translate.instant(field.field) : '';
    return keys.length ? `${fieldName} (${keys.join(' / ')})` : fieldName;
  }

  private resetColumnSettings(): void {
    Object.assign(this.templateItem, {
      calculation: '',
      factorOperator: 'NONE',
      factor: 1,
      numberformat: '',
      dateformat: '',
      seperator: '',
      mapping: '',
      prefix: '',
      suffix: '',
      isSeparatorMode: false,
      maxCharacters: null,
      splitSeparator: '',
      section: 1
    });
    this.splitSeparatorValue = '';
  }

  ngOnInit() {
    this.calculation = [
      new Select('', '', ''),
      new Select(this.translate.instant('g in kg'), this.translate.instant('kg in g'), 'GINKG'),
      new Select(this.translate.instant('t in kg'), this.translate.instant('kg in t'), 'TINKG'),
      new Select(this.translate.instant('mm in m'), this.translate.instant('m in mm'), 'MMINM'),
      new Select(this.translate.instant('cm in m'), this.translate.instant('m in cm'), 'CMINM'),
      new Select(this.translate.instant('Faktor'), this.translate.instant('Faktor'), 'FACTOR')
    ];

    this.displayExpr = 'displayExprExport';
    if (!this.exportMode) {
      this.displayExpr = 'displayExpr';
      this.calculation.splice(
        5,
        0,
        new Select(this.translate.instant('Stück in C62'), this.translate.instant('C62 in Stück'), 'STKINC62')
      );
    }

    this.numberformat = [
      new Select('', '', ''),
      new Select('1.900,00', '1.900,00', ','),
      new Select('1,900.00', '1,900.00', '.')
    ];

    this.dateformat = [
      new Select('', '', ''),
      new Select('TT.MM.JJJJ (31.12.2026)', 'TT.MM.JJJJ (31.12.2026)', 'dd.MM.yyyy'),
      new Select('TT.MM.JJ (31.12.26)', 'TT.MM.JJ (31.12.26)', 'dd.MM.yy'),
      new Select('T.M.JJ (31.12.26)', 'T.M.JJ (31.12.26)', 'd.M.yy'),
      new Select('JJJJ-MM-TT (2026-12-31)', 'JJJJ-MM-TT (2026-12-31)', 'yyyy-MM-dd'),
      new Select('JJJJ/MM/TT (2026/12/31)', 'JJJJ/MM/TT (2026/12/31)', 'yyyy\'/\'MM\'/\'dd'),
      new Select('JJJJ/TT/MM (2026/31/12)', 'JJJJ/TT/MM (2026/31/12)', 'yyyy\'/\'dd\'/\'MM'),
      new Select('TT. MMM JJJJ (31. Dez 2026)', 'TT. MMM JJJJ (31. Dez 2026)', 'dd. MMM yyyy'),
      new Select('TT. MMMM JJJJ (31. Dezember 2026)', 'TT. MMMM JJJJ (31. Dezember 2026)', 'dd. MMMM yyyy')
    ];

    this.seperator = [
      new Select('', '', ''),
      new Select(this.translate.instant('Leerzeichen'), this.translate.instant('Leerzeichen'), ' '),
      new Select('_', '_', '_'),
      new Select('|', '|', '|'),
      new Select(',', ',', ','),
      new Select('.', '.', '.'),
      new Select(';', ';', ';')
    ];

    this.operators = [
      new Select('', '', 'NONE'),
      new Select(this.translate.instant('Addieren'), this.translate.instant('Addieren'), 'ADDITION'),
      new Select(this.translate.instant('Subtrahieren'), this.translate.instant('Subtrahieren'), 'SUBTRACTION'),
      new Select(this.translate.instant('Multiplizieren'), this.translate.instant('Multiplizieren'), 'MULTIPLICATION'),
      new Select(this.translate.instant('Dividieren'), this.translate.instant('Dividieren'), 'DIVISION')
    ];

    this.splitModeOptions = [
      new Select(this.translate.instant('Zeichenzahl'), this.translate.instant('Zeichenzahl'), false as any),
      new Select(this.translate.instant('Trennzeichen'), this.translate.instant('Trennzeichen'), true as any)
    ];

    this.splitSeparator = [
      new Select('', '', ''),
      new Select(this.translate.instant('Leerzeichen'), this.translate.instant('Leerzeichen'), ' '),
      new Select('_', '_', '_'),
      new Select('|', '|', '|'),
      new Select(',', ',', ','),
      new Select('.', '.', '.'),
      new Select(';', ';', ';')
    ];

    this.mappingApplicationOptions = [
      new Select(this.translate.instant('Vor Anwendung der Formel'), this.translate.instant('Vor Anwendung der Formel'), false as any),
      new Select(this.translate.instant('Nach Anwendung der Formel'), this.translate.instant('Nach Anwendung der Formel'), true as any)
    ];

    if (this.excelElement) {
      this.templateItem = this.excelElement.toTemplateItem();
    }

    this.ensureDefaults();
    this.syncSplitSeparatorFromTemplateItem();
  }

  private syncSplitSeparatorFromTemplateItem(): void {
    if (!this.templateItem) return;

    if (this.templateItem.isSeparatorMode === true) {
      this.splitSeparatorValue = this.templateItem.splitSeparator ?? '';
    } else {
      this.splitSeparatorValue = '';
    }
  }

  private ensureDefaults(): void {
    if (!this.templateItem) return;

    if (this.templateItem.isSeparatorMode === undefined || this.templateItem.isSeparatorMode === null) {
      this.templateItem.isSeparatorMode = false;
    }
    
    if (this.templateItem.section === undefined || this.templateItem.section === null || this.templateItem.section < 1) {
      this.templateItem.section = 1;
    }

    if (this.templateItem.splitSeparator === undefined || this.templateItem.splitSeparator === null) {
      this.templateItem.splitSeparator = '';
    }
  }

  update(): void {
    if (!this.templateItem) return;

    if (this.templateItem.isSeparatorMode === true) {
      this.templateItem.splitSeparator = (this.splitSeparatorValue ?? '').toString();
    }

    if (this.excelElement) {
      this.excelElement.update(this.templateItem);
    }

    this.onUpdate.emit(this.excelElement);
  }
}
